import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { createLink } from '@/app/navigation/stack-links';
import { cn } from '@/lib/utils';

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

interface TabsListProps
  extends React.ComponentProps<typeof TabsPrimitive.List> {
  /**
   * When true, tabs will scroll horizontally on smaller screens
   * instead of wrapping or shrinking
   */
  scrollable?: boolean;
}

function TabsList({ className, scrollable, ...props }: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        `bg-muted text-muted-foreground inline-flex h-9 items-center justify-center
        rounded-lg p-[3px]`,
        scrollable
          ? // Scrollable: full width container with horizontal scroll
            `w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden`
          : // Default: fit content width
            'w-fit',
        className,
      )}
      {...props}
    />
  );
}

const TriggerLink = createLink(TabsPrimitive.Trigger);

function TabsLink({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & { to: string }) {
  return (
    <TriggerLink
      data-slot="tabs-trigger"
      className={cn(
        `data-[state=active]:bg-background dark:data-[state=active]:text-foreground
        focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring
        dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30
        text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1
        items-center justify-center gap-1.5 rounded-md border border-transparent px-2
        py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow]
        focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none
        disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none
        [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        `data-[state=active]:bg-background dark:data-[state=active]:text-foreground
        focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring
        dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30
        text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1
        items-center justify-center gap-1.5 rounded-md border border-transparent px-2
        py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow]
        focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none
        disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none
        [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsLink, TabsContent };
